import { total, TIE_PAYS, winner, type BaccaratSide, type BaccaratState, type Coup } from '../../engine/games/baccarat';
import type { Card } from '../../engine/cards';
import { money } from '../../format';
import { cardsIn, PlayingCard } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

const CARD_MS = 480;
const LUCK_HOLD_MS = 1500;
const END_HOLD_MS = 400;
const SIDES: BaccaratSide[] = ['player', 'banker', 'tie'];
const NAMES: Record<BaccaratSide, string> = { player: 'Player', banker: 'Banker', tie: 'Tie' };
const ORDER: BaccaratSide[] = ['banker', 'player', 'tie']; // the engine's cycle order

/** Cards shown after each deal step: Player, Banker, Player, Banker, then
 *  any third cards (the order the engine's frames use). */
function dealSteps(coup: Coup): [number, number][] {
  const steps: [number, number][] = [[1, 0], [1, 1], [2, 1], [2, 2]];
  if (coup.player.length === 3) steps.push([3, 2]);
  if (coup.banker.length === 3) steps.push([coup.player.length, 3]);
  return steps;
}

/** The losing coup a ★ second chance replaced, from the engine's frame. */
function parseCoup(frame: string): Coup | null {
  const lines = frame.split('\n');
  const p = lines.find((l) => l.startsWith('Player:'));
  const b = lines.find((l) => l.startsWith('Banker:'));
  if (!p || !b) return null;
  return { player: cardsIn(p), banker: cardsIn(b) };
}

/** One side's hand. Once a coup is over it's shown as the last hand: dimmed,
 *  with a "Won" tag on the winning side instead of a bright border, so it
 *  can't be mistaken for your next bet (user, 2026-09-27). */
function Hand({ name, cards, shown, tag, past }: { name: string; cards: Card[]; shown: number; tag: string | null; past: boolean }) {
  const visible = cards.slice(0, shown);
  return (
    <div className={`bac-hand${past ? ' past' : ''}`}>
      <div className="bac-name">
        {name}
        {tag && <span className="won-tag">{tag}</span>}
        <span className="bac-total">{visible.length ? total(visible) : ''}</span>
      </div>
      <div className="cards">{visible.length ? visible.map((c, i) => <PlayingCard key={i} card={c} />) : <PlayingCard hidden />}</div>
    </div>
  );
}

export function BaccaratTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.baccarat?.state as BaccaratState | undefined;
  const anim = snapshot.anim;
  const luckFrame = secondChanceFrame(snapshot);
  const first = luckFrame ? parseCoup(luckFrame) : null;
  const coup = state?.last ?? null;
  const firstMs = first ? dealSteps(first).length * CARD_MS + LUCK_HOLD_MS : 0;
  const elapsed = useAnimClock(snapshot, actions, firstMs + (coup ? dealSteps(coup).length * CARD_MS : 0) + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();

  // The losing coup is dealt out first; then the second chance, then the new coup.
  useLuckMoment(snapshot, elapsed, first ? dealSteps(first).length * CARD_MS + 200 : null, () => ({ what: 'A losing coup is dealt again.', saved: !!state?.lucky }), setBurst);

  // Which coup is on the table, and how much of it is dealt so far.
  let shownCoup = coup;
  let shown: [number, number] = coup ? [coup.player.length, coup.banker.length] : [0, 0];
  if (anim && coup) {
    const inFirst = !!first && elapsed < firstMs;
    shownCoup = inFirst ? first! : coup;
    const t = inFirst ? elapsed : elapsed - firstMs;
    const steps = dealSteps(shownCoup);
    shown = steps[Math.min(steps.length - 1, Math.floor(t / CARD_MS))]!;
  }
  const settled = !!coup && !anim;
  const result = settled ? winner(coup!) : null;

  // Choosing a spot: the engine cycles Banker -> Player -> Tie, so step to it.
  const pick = (side: BaccaratSide) => {
    if (!state || anim) return;
    const steps = (ORDER.indexOf(side) - ORDER.indexOf(state.side) + ORDER.length) % ORDER.length;
    for (let i = 0; i < steps; i++) actions.tableOption('side');
  };
  const side = state?.side ?? 'banker';
  const net = state ? state.returned - state.lastBet : 0;
  const summary = settled && state
    ? `Last hand: ${result === 'tie' ? 'Tie' : `${NAMES[result!]} won`} · your ${NAMES[state.lastSide]} bet ${net > 0 ? `${state.lucky ? '★ ' : ''}+${money(net)}` : net === 0 ? 'pushed' : `−${money(-net)}`}`
    : undefined;

  return (
    <>
      <Stage className="bac-stage">
        <Burst burst={burst} fading={fading} pending={!!anim && elapsed < firstMs + (coup ? dealSteps(coup).length * CARD_MS : 0)} />
        {/* Held in place (hidden before the first hand) so the hands never move. */}
        <div className="bac-spots-label" style={{ visibility: settled ? "visible" : "hidden" }}>Last hand</div>
        <div className="bac-hands" onClick={anim ? actions.skip : undefined}>
          <Hand name="Player" cards={shownCoup?.player ?? []} shown={shown[0]} tag={result === 'tie' ? 'Tie' : result === 'player' ? 'Won' : null} past={settled} />
          <Hand name="Banker" cards={shownCoup?.banker ?? []} shown={shown[1]} tag={result === 'tie' ? 'Tie' : result === 'banker' ? 'Won' : null} past={settled} />
        </div>
        <div className="bac-spots-label">Your bet</div>
        <div className="bac-spots" role="radiogroup" aria-label="Your bet">
          {SIDES.map((s) => (
            <button key={s} role="radio" aria-checked={side === s} className={`bac-spot${side === s ? ' on' : ''}`} onClick={() => pick(s)} disabled={!!anim}>
              <span className="spot-name">{NAMES[s]}</span>
              <span className="spot-pays">{s === 'tie' ? `${TIE_PAYS}:1` : s === 'banker' ? '1:1 · 6 pays 1:2' : '1:1'}</span>
            </button>
          ))}
        </div>
        <StatusLine snapshot={snapshot} result={summary} tone={net > 0 ? 'win' : net < 0 ? 'loss' : ''} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['side']} onAct={() => setBurst(null)} />
    </>
  );
}
